// import hooks
import { yupResolver } from '@hookform/resolvers/yup';
import { isNil } from 'ramda';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import useGetDHTClaById from '../hooks/useGetDHTClaById';

//import schema
import { schema } from '../utils/schema';

// import api's and mutations
import { DHT_CLA_SETTINGS } from 'RestAPIs/endpoints';
import { useUpdateResource } from 'RestAPIs/useUpdateResource';

// import components
import { LoadingButton } from '@mui/lab';
import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import DrawerFormSkeleton from 'components/shared/CRUD/components/DrawerFormSkeleton';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { TextField } from '@mui/material';

const EditDHTClaSettingsForm = ({ handleClose, rowId }: FormComponentProps) => {
  const { t } = useTranslation('common');
  const { mutateAsync: edit, isLoading } = useUpdateResource({
    url: DHT_CLA_SETTINGS,
    id: rowId!,
    invalidateQueryKey: ['DHTClaSettings', 'list']
  });
  const { data, isFetching } = useGetDHTClaById(
    !isNil(rowId) ? Number(rowId) : undefined
  );

  const methods = useForm({
    defaultValues: schema.cast(undefined),
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (data) {
      methods.reset({
        ...schema.cast(data)
      });
    }
  }, [data]);

  const { control, handleSubmit } = methods;

  const submit = async (formValues) => {
    await edit(formValues).then(handleClose);
  };

  if (isFetching) {
    return <DrawerFormSkeleton count={3} />;
  }

  return (
    <MultiStepForm methods={methods} onSubmit={handleSubmit(submit)}>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.basis')}
      >
        <GridContainer>
          <GridCompact6>
            <TextField
              label={t('DHTClaSettings.grid.code')}
              variant="outlined"
              size="small"
              type="text"
              disabled
              defaultValue={data?.dutyTypeDyn.code ?? ''}
            />
          </GridCompact6>
          <GridCompact12>
            <TextField
              label={t('DHTClaSettings.grid.description')}
              variant="outlined"
              size="small"
              type="text"
              disabled
              defaultValue={data?.dutyTypeDyn.description ?? ''}
            />
          </GridCompact12>
          <GridCompact6>
            <TextField
              label={t('DHTClaSettings.grid.claId')}
              variant="outlined"
              size="small"
              type="text"
              disabled
              defaultValue={data?.claCode ?? ''}
            />
          </GridCompact6>
          <GridCompact6>
            <TextField
              label={t('DHTClaSettings.grid.dccSvc')}
              variant="outlined"
              size="small"
              type="text"
              disabled
              defaultValue={data?.dutyTypeDyn.dccSvc ?? ''}
            />
          </GridCompact6>
        </GridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.codes')}
      >
        <GridContainer>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcYhsLc')}
              control={control}
              variant="outlined"
              type="text"
              name="hcYhsLc"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcYhsLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="hcYhsLcSec"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcPayLc')}
              control={control}
              variant="outlined"
              type="text"
              name="hcPayLc"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.hcPayLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="hcPayLcSec"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcYhsLc')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcYhsLc"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcYhsLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcYhsLcSec"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcPayLc')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcPayLc"
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledTextField
              label={t('DHTClaSettings.grid.zhcPayLcSec')}
              control={control}
              variant="outlined"
              type="text"
              name="zhcPayLcSec"
            />
          </GridCompact6>
        </GridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('DHTClaSettings.formSectionLabels.validity')}
      >
        <GridContainer>
          <GridCompact6>
            <ControlledDatePicker
              control={control}
              name="dutyTypeDyn.valid.from"
              variant="outlined"
              label={t('validFrom')}
              disabled
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledDatePicker
              control={control}
              name="dutyTypeDyn.valid.to"
              variant="outlined"
              label={t('validTo')}
              disabled
            />
          </GridCompact6>
        </GridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          type="submit"
          loading={isLoading}
          autoFocus
          color="primary"
          variant="contained"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default EditDHTClaSettingsForm;
